import React, { useState, useEffect, useMemo } from 'react'
import { View, StyleSheet, FlatList, Platform } from 'react-native'
import { Text, Button, Spinner, RangeDatepicker, Select, SelectItem, Toggle, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { queryDatabaseWriteLogs, formatLogDetails, exportLogs } from '../../utils/databaseWriteLogViewer'
import { exportToTextFile } from '../../utils/exportUtils'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { useToast } from 'react-native-toast-notifications'
import HeaderIconButton from '../../components/HeaderIconButton'

export default function DatabaseWriteLogsScreen({ navigation, route }) {
  const { t } = useTranslation()
  const toast = useToast()
  const theme = useTheme()
  const [logs, setLogs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [lastDoc, setLastDoc] = useState(null)
  const [selectedLog, setSelectedLog] = useState(null)
  const [range, setRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    endDate: new Date()
  })
  const [store, setStore] = useState(route.params?.store || 'all')
  const [functionName, setFunctionName] = useState('all')
  const [hideZeroOperations, setHideZeroOperations] = useState(false)
  const _rangePicker = React.createRef()
  
  // Filter logs based on hideZeroOperations toggle
  const filteredLogs = useMemo(() => {
    if (hideZeroOperations) {
      return logs.filter(log => (log.summary?.totalOperations || 0) > 0)
    }
    return logs
  }, [logs, hideZeroOperations])

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          iconName='download-outline'
          onPress={handleExportLogs}
        />
      ),
    })
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [navigation, logs])
  
  // Load logs when filters change
  useEffect(() => {
    if (range.startDate && range.endDate) {
      loadLogs(true) // Reset and load from beginning
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.startDate, range.endDate, store, functionName])

  const loadLogs = async (reset = false) => {
    if (!range.startDate || !range.endDate) {
      if (!reset) {
        toast.show('Выберите диапазон дат', {type: 'warning'})
      }
      return
    }

    try {
      if (reset) {
        setIsLoading(true)
        setLogs([])
        setLastDoc(null)
        setHasMore(false)
      } else {
        setIsLoadingMore(true)
      }
      
      // Prepare date range for query (end date should be end of day)
      const endDate = new Date(range.endDate)
      endDate.setHours(23, 59, 59, 999)
      
      const filters = {
        startDate: range.startDate,
        endDate: endDate,
        limit: 50 // Fixed page size for lazy loading
      }
      
      if (store && store !== 'all') {
        filters.store = store
      }
      
      if (functionName && functionName !== 'all') {
        filters.functionName = functionName
      }
      
      // Add pagination cursor if loading more
      if (!reset && lastDoc) {
        filters.startAfter = lastDoc
      }
      
      const result = await queryDatabaseWriteLogs(filters)
      
      if (reset) {
        setLogs(result.logs)
        setLastDoc(result.lastDoc)
        setHasMore(result.hasMore)
        
        if (result.logs.length === 0) {
          toast.show('Логи не найдены', {type: 'info'})
        } else {
          toast.show(`Найдено ${result.logs.length} логов`, {type: 'success'})
        }
      } else {
        // Append new logs
        setLogs(prevLogs => [...prevLogs, ...result.logs])
        setLastDoc(result.lastDoc)
        setHasMore(result.hasMore)
      }
    } catch (error) {
      console.error('Error loading logs:', error)
      toast.show('Ошибка при загрузке логов', {type: 'error'})
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }
  
  const loadMoreLogs = () => {
    if (!isLoadingMore && hasMore && !isLoading) {
      loadLogs(false)
    }
  }

  const handleExportLogs = async () => {
    const logsToExport = hideZeroOperations ? filteredLogs : logs
    
    if (logsToExport.length === 0) {
      toast.show('Нет логов для экспорта', {type: 'warning'})
      return
    }

    try {
      setIsLoading(true)
      
      // Generate full detailed export text
      const exportText = exportLogs(logsToExport)
      
      const filename = `database-write-logs-details-${format(range.startDate, 'd-M-y')}-${format(range.endDate, 'd-M-y')}${store && store !== 'all' ? `-store-${store}` : ''}${hideZeroOperations ? '-no-zero-ops' : ''}`
      
      await exportToTextFile(exportText, filename)
      
      toast.show(`Экспортировано ${logsToExport.length} логов`, {type: 'success'})
    } catch (error) {
      console.error('Error exporting logs:', error)
      toast.show('Ошибка при экспорте логов', {type: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <View style={styles.container}>
      {isLoading && logs.length === 0 ? (
        <View style={styles.spinnerContainer}>
          <Spinner />
        </View>
      ) : (
        <FlatList
          data={filteredLogs}
          keyExtractor={(item) => item.id}
          ListHeaderComponent={() => (
            <>
              <View style={styles.filtersContainer}>
                <Text category='h5' style={styles.sectionTitle}>Фильтры</Text>
                
                <RangeDatepicker
                  min={new Date('2020-12-01')}
                  range={range}
                  ref={_rangePicker}
                  onSelect={(newRange) => {
                    setRange(newRange)
                  }}
                />
                
                <StoreChooserHorizontal 
                  store={store} 
                  style={styles.storeChooser} 
                  showAll={true} 
                  onChange={(newStore) => {
                    setStore(newStore)
                  }} 
                />
                
                <Select
                  label='Функция'
                  selectedIndex={functionName === 'all' ? 0 : functionName === 'countEmployeeBonuses' ? 1 : 2}
                  value={functionName === 'all' ? 'Все' : functionName === 'countEmployeeBonuses' ? 'countEmployeeBonuses' : 'validateDailyStats'}
                  onSelect={(index) => {
                    if (index.row === 0) setFunctionName('all')
                    else if (index.row === 1) setFunctionName('countEmployeeBonuses')
                    else if (index.row === 2) setFunctionName('validateDailyStats')
                  }}
                  style={styles.select}
                >
                  <SelectItem title='Все' />
                  <SelectItem title='countEmployeeBonuses' />
                  <SelectItem title='validateDailyStats' />
                </Select>
                
                <View style={styles.toggleContainer}>
                  <Toggle
                    checked={hideZeroOperations}
                    onChange={setHideZeroOperations}
                    style={styles.toggle}
                  >
                    Скрыть логи с 0 операций
                  </Toggle>
                </View>
                
                <Button 
                  onPress={() => loadLogs(true)}
                  disabled={isLoading}
                  style={styles.loadButton}
                >
                  {isLoading ? 'Загрузка...' : 'Обновить логи'}
                </Button>
              </View>
              
              {filteredLogs.length > 0 && (
                <View style={styles.logsHeader}>
                  <Text category='h5' style={styles.sectionTitle}>
                    Найдено логов: {filteredLogs.length}{hideZeroOperations && logs.length !== filteredLogs.length ? ` (${logs.length} всего)` : ''}
                  </Text>
                  
                  <Button 
                    onPress={handleExportLogs}
                    style={styles.exportButton}
                    appearance='outline'
                    size='small'
                  >
                    Экспорт в TXT
                  </Button>
                </View>
              )}
            </>
          )}
          renderItem={({ item: log, index }) => {
              const timestamp = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp)
              const duration = log.duration || 0
              
              return (
                <View style={[styles.logCard, { backgroundColor: theme['background-basic-color-2'] }]}>
                  <View style={styles.logHeader}>
                    <Text category='s1' style={styles.logTitle}>
                      {index + 1}. {log.functionName}
                    </Text>
                    <Button
                      size='small'
                      appearance={selectedLog?.id === log.id ? 'filled' : 'outline'}
                      onPress={() => {
                        setSelectedLog(selectedLog?.id === log.id ? null : log)
                      }}
                    >
                      {selectedLog?.id === log.id ? 'Скрыть детали' : 'Показать детали'}
                    </Button>
                  </View>
                  
                  <View style={styles.logSummary}>
                    <Text style={styles.logText}>
                      📅 {format(timestamp, 'yyyy-MM-dd HH:mm:ss')}
                    </Text>
                    <Text style={styles.logText}>
                      ⏱️ Длительность: {duration}ms
                    </Text>
                    {log.context?.store && (
                      <Text style={styles.logText}>
                        🏪 Магазин: {log.context.store}
                      </Text>
                    )}
                    {log.context?.screen && (
                      <Text style={styles.logText}>
                        📱 Экран: {log.context.screen}
                      </Text>
                    )}
                    {log.context?.platform && (
                      <Text style={styles.logText}>
                        💻 Платформа: {log.context.platform}
                      </Text>
                    )}
                    {log.context?.callerFile && (
                      <Text style={styles.logText}>
                        📄 Файл: {log.context.callerFile}
                      </Text>
                    )}
                    {log.context?.callerFunction && (
                      <Text style={styles.logText}>
                        🔧 Функция: {log.context.callerFunction}
                      </Text>
                    )}
                    <Text style={styles.logText}>
                      📊 Операций: {log.summary?.totalOperations || 0} 
                      ({log.summary?.directWrites || 0} прямых, {log.summary?.batches || 0} пакетов)
                    </Text>
                    <Text style={styles.logText}>
                      📁 Коллекций: {log.summary?.collectionsAffected || 0}
                    </Text>
                  </View>

                  {selectedLog?.id === log.id && (
                    <View style={[styles.logDetails, { backgroundColor: theme['background-basic-color-1'], borderColor: theme['border-basic-color-3'] }]}>
                      <Text style={styles.detailsText} selectable>
                        {formatLogDetails(log)}
                      </Text>
                    </View>
                  )}
                </View>
              )
            }}
            onEndReached={loadMoreLogs}
            onEndReachedThreshold={0.5}
            ListFooterComponent={() => {
              if (isLoadingMore) {
                return (
                  <View style={styles.spinnerContainer}>
                    <Spinner size='small' />
                  </View>
                )
              }
              if (!hasMore && logs.length > 0) {
                return (
                  <Text style={[styles.noMoreLogsText, { color: theme['text-hint-color'] }]}>
                    Все логи загружены
                  </Text>
                )
              }
              return <View style={{height: 50}} />
            }}
            ListEmptyComponent={() => {
              if (isLoading) {
                return null
              }
              if (range.startDate && range.endDate) {
                if (hideZeroOperations && logs.length > 0 && filteredLogs.length === 0) {
                  return (
                    <Text style={[styles.noLogsText, { color: theme['text-hint-color'] }]}>
                      Все логи имеют 0 операций. Отключите фильтр для просмотра.
                    </Text>
                  )
                }
                return (
                  <Text style={[styles.noLogsText, { color: theme['text-hint-color'] }]}>
                    Логи не найдены для указанных фильтров
                  </Text>
                )
              }
              return (
                <Text style={[styles.noLogsText, { color: theme['text-hint-color'] }]}>
                  Выберите диапазон дат для загрузки логов
                </Text>
              )
            }}
            contentContainerStyle={styles.listContent}
            scrollEnabled={true}
            style={styles.flatList}
          />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  filtersContainer: {
    padding: 20,
    paddingBottom: 20,
  },
  sectionTitle: {
    marginTop: 20,
    marginBottom: 10,
  },
  storeChooser: {
    marginVertical: 10,
  },
  select: {
    marginVertical: 10,
  },
  toggleContainer: {
    marginVertical: 10,
  },
  toggle: {
    marginTop: 5,
  },
  loadButton: {
    marginTop: 10,
    marginBottom: 10,
  },
  exportButton: {
    marginLeft: 10,
  },
  logsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  flatList: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  spinnerContainer: {
    alignItems: 'center',
    padding: 20,
  },
  logCard: {
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
  },
  logHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  logTitle: {
    flex: 1,
    fontWeight: 'bold',
  },
  logSummary: {
    marginTop: 10,
  },
  logText: {
    marginVertical: 3,
    fontSize: 14,
  },
  logDetails: {
    marginTop: 15,
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
  },
  detailsText: {
    fontSize: 12,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  noLogsText: {
    textAlign: 'center',
    marginTop: 40,
    fontSize: 16,
    padding: 20,
  },
  noMoreLogsText: {
    textAlign: 'center',
    padding: 20,
    fontSize: 14,
  },
})

